// src/components/teams/hooks/useTeamData.js
import { useEffect, useState } from 'react';
import { doc, collection, getDoc, getDocs, query, where } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { db } from '../../../firebase-client';
import { isRecoverableFirestoreListenTargetError } from '../../../utils/firestoreSubscriptions';
import { normalizeTeamDocuments } from '../utils/documentUtils';
import { normalizePermissionKeys } from '../utils/adminPermissions';

const TEAM_DOC_LOAD_TIMEOUT_MS = 12000;
const TEAM_DOC_FALLBACK_READ_DELAY_MS = 1500;

export function useTeamData(teamId, refreshKey = 0, user, authLoading) {
  const [team, setTeam] = useState(null);
  const [memberProfiles, setMemberProfiles] = useState([]);
  const [adminProfiles, setAdminProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userId = user?.uid || '';
  const teamReady = Boolean(team);
  const adminIds = Array.isArray(team?.admins) ? team.admins : [];
  const adminIdsKey = adminIds.join('|');
  const ownAssignment = team?.myAdminAssignment || (
    Array.isArray(team?.adminAssignments)
      ? team.adminAssignments.find((assignment) => (assignment.uid || assignment.id) === userId)
      : null
  );
  const ownPermissionKeys = normalizePermissionKeys(ownAssignment?.permissionKeys);
  const ownPermissionKeysKey = ownPermissionKeys.join('|');

  // --- Subscribe to team doc + join form config in real time ---
  useEffect(() => {
    if (!teamId || authLoading || !userId) return;
    setLoading(true);
    setError(null);
    const teamRef = doc(db, 'teams', teamId);
    let baseTeamResolved = false;
    const applyBaseTeamSnapshot = (snap) => {
      baseTeamResolved = true;
      clearTimeout(teamDocTimeoutId);
      if (!snap.exists()) {
        setError('Team not found');
        setTeam(null);
        setLoading(false);
        return;
      }
      const base = {
        id: snap.id,
        ...snap.data()
      };
      setTeam(prev => ({
        ...(prev || {}),
        ...base
      }));
      setLoading(false);
    };
    const teamDocTimeoutId = setTimeout(() => {
      if (baseTeamResolved) return;
      setError('Timed out loading club details. Please refresh and try again.');
      setLoading(false);
    }, TEAM_DOC_LOAD_TIMEOUT_MS);
    const fallbackReadId = setTimeout(() => {
      if (baseTeamResolved) return;
      getDoc(teamRef)
        .then((snap) => {
          if (baseTeamResolved) return;
          applyBaseTeamSnapshot(snap);
        })
        .catch(() => {
          if (baseTeamResolved) return;
        });
    }, TEAM_DOC_FALLBACK_READ_DELAY_MS);
    const unsubTeam = safeOnSnapshot(teamRef, snap => {
      applyBaseTeamSnapshot(snap);
    }, err => {
      if (isRecoverableFirestoreListenTargetError(err)) return;
      baseTeamResolved = true;
      clearTimeout(teamDocTimeoutId);
      clearTimeout(fallbackReadId);
      setError(err.message);
      setLoading(false);
    });

    // Join Form Config lives as a doc; subscribe too
    const cfgRef = doc(db, 'teams', teamId, 'joinFormConfig', 'config');
    const unsubCfg = safeOnSnapshot(cfgRef, snap => {
      const joinFormConfig = snap.exists() ? snap.data() : {
        enabled: false,
        questions: []
      };
      setTeam(prev => prev ? {
        ...prev,
        joinFormConfig
      } : {
        id: teamId,
        joinFormConfig
      });
    }, err => {
      if (err?.code === 'permission-denied') return;

      // Non-fatal — keep going without config
    });

    // Profile Fields Config lives as a doc; subscribe too
    const profileRef = doc(db, 'teams', teamId, 'profileFields', 'config');
    const unsubProfile = safeOnSnapshot(profileRef, snap => {
      const profileFieldsConfig = snap.exists() ? snap.data() : {
        enabled: false,
        fields: []
      };
      setTeam(prev => prev ? {
        ...prev,
        profileFieldsConfig
      } : {
        id: teamId,
        profileFieldsConfig
      });
    }, err => {
      if (err?.code === 'permission-denied') return;

      // Non-fatal — keep going without config
    });

    const documentsRef = collection(db, 'teams', teamId, 'documents');
    const unsubDocuments = safeOnSnapshot(documentsRef, snap => {
      const documents = normalizeTeamDocuments(snap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })));
      setTeam(prev => prev ? {
        ...prev,
        documents
      } : {
        id: teamId,
        documents
      });
    }, err => {
      if (err?.code === 'permission-denied') return;
    });
    const rolesRef = collection(db, 'teams', teamId, 'adminRoles');
    const unsubAdminRoles = safeOnSnapshot(rolesRef, snap => {
      const adminRoles = snap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        permissionKeys: normalizePermissionKeys(docSnap.data()?.permissionKeys),
      })).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      setTeam(prev => prev ? {
        ...prev,
        adminRoles
      } : {
        id: teamId,
        adminRoles
      });
    }, err => {
      if (err?.code === 'permission-denied') return;
    });
    const assignmentsRef = collection(db, 'teams', teamId, 'adminAssignments');
    const unsubAdminAssignments = safeOnSnapshot(assignmentsRef, snap => {
      const adminAssignments = snap.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        permissionKeys: normalizePermissionKeys(docSnap.data()?.permissionKeys),
      }));
      setTeam(prev => prev ? {
        ...prev,
        adminAssignments
      } : {
        id: teamId,
        adminAssignments
      });
    }, err => {
      if (err?.code === 'permission-denied') return;
    });
    const ownAssignmentRef = doc(db, 'teams', teamId, 'adminAssignments', userId);
    const unsubMyAdminAssignment = safeOnSnapshot(ownAssignmentRef, snap => {
      const myAdminAssignment = snap.exists() ? {
        id: snap.id,
        ...snap.data(),
        permissionKeys: normalizePermissionKeys(snap.data()?.permissionKeys),
      } : null;
      setTeam(prev => prev ? {
        ...prev,
        myAdminAssignment
      } : {
        id: teamId,
        myAdminAssignment
      });
    }, err => {
      if (err?.code === 'permission-denied') return;
    });
    return () => {
      clearTimeout(teamDocTimeoutId);
      clearTimeout(fallbackReadId);
      unsubTeam();
      unsubCfg();
      unsubProfile();
      unsubDocuments();
      unsubAdminRoles();
      unsubAdminAssignments();
      unsubMyAdminAssignment();
    };
  }, [teamId, refreshKey, authLoading, userId]);

  // --- Resolve members we’re allowed to see (live) then fetch profiles ---
  useEffect(() => {
    if (authLoading || !userId || !teamId || !teamReady) return;
    const uid = userId;
    const adminUids = adminIdsKey.split('|').filter(Boolean);
    const isAdmin = adminUids.includes(uid);
    const permissionKeys = ownPermissionKeysKey.split('|').filter(Boolean);
    const canListMembers = isAdmin
      || permissionKeys.includes('membership.manage')
      || permissionKeys.includes('members.manage')
      || permissionKeys.includes('members.export')
      || permissionKeys.includes('communications.manage')
      || permissionKeys.includes('mentoring.manage')
      || permissionKeys.includes('rideDeclarations.manage')
      || permissionKeys.includes('rideReports.manage');
    let unsubMembers;
    let cancelled = false;
    async function hydrateProfiles(memberDocs, adminIds) {
      try {
        const membersById = new Map((memberDocs || []).map(memberDoc => [memberDoc.id, memberDoc]));
        const memberIds = Array.from(membersById.keys());

        // Unique IDs, Firestore `in` capped at 10 – chunk
        const all = Array.from(new Set([...memberIds, ...adminIds]));
        const chunks = [];
        for (let i = 0; i < all.length; i += 10) chunks.push(all.slice(i, i + 10));
        const usersById = new Map();
        for (const c of chunks) {
          if (c.length === 0) continue;
          const qUsers = query(collection(db, 'users'), where('__name__', 'in', c));
          const snap = await getDocs(qUsers);
          snap.forEach(d => usersById.set(d.id, {
            id: d.id,
            ...d.data()
          }));
        }
        const nameFromUser = u => u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.id;
        if (!cancelled) {
          setAdminProfiles(adminIds.map(adminId => {
            const userDoc = usersById.get(adminId) || {
              id: adminId
            };
            return {
              id: adminId,
              name: nameFromUser(userDoc)
            };
          }));
          setMemberProfiles(memberIds.map(memberId => {
            const userDoc = usersById.get(memberId) || {
              id: memberId
            };
            const memberDoc = membersById.get(memberId) || {};
            return {
              id: memberId,
              name: nameFromUser(userDoc),
              familyRoles: userDoc.family_roles || {},
              tags: memberDoc.tags || {},
              learnToRow: memberDoc.learnToRow || null,
              learnToSail: memberDoc.learnToSail || null,
              courseEnrollment: memberDoc.courseEnrollment || null,
              courseOnlyAccess: memberDoc.courseOnlyAccess === true,
              profileFields: memberDoc.profileFields || {},
              joinAnswers: memberDoc.joinAnswers || {},
              role: memberDoc.role || null,
              joinedAt: memberDoc.joinedAt || memberDoc.joinDate || null,
              membershipPaymentOwnerUid: memberDoc.membershipPaymentOwnerUid || null,
              membershipCoverageType: memberDoc.membershipCoverageType || null,
              membershipPlanType: memberDoc.membershipPlanType || null,
              membershipPlanTierId: memberDoc.membershipPlanTierId || null,
              membershipPlanLabel: memberDoc.membershipPlanLabel || null,
              membershipPlanSnapshot: memberDoc.membershipPlanSnapshot || null,
              membershipCoverageFamilyId: memberDoc.membershipCoverageFamilyId || null,
              membershipCoveredMemberIds: Array.isArray(memberDoc.membershipCoveredMemberIds)
                ? memberDoc.membershipCoveredMemberIds
                : [],
              membershipCoveredByUid: memberDoc.membershipCoveredByUid || null,
              membershipCoveredByFamilyId: memberDoc.membershipCoveredByFamilyId || null
            };
          }));
        }
      } catch (err) {
        if (!cancelled && !isRecoverableFirestoreListenTargetError(err)) setError(err.message);
      }
    }

    // Live membership source differs by role
    if (canListMembers) {
      // Permission roles that need a member picker/list can see the member directory.
      const membersRef = collection(db, 'teams', teamId, 'members');
      unsubMembers = safeOnSnapshot(membersRef, snap => {
        const memberDocs = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        hydrateProfiles(memberDocs, adminUids);
      }, err => {
        if (err?.code === 'permission-denied') return;
        if (isRecoverableFirestoreListenTargetError(err)) return;
        setError(err.message);
      });
    } else {
      // Non-admins: only their own membership doc
      const myRef = doc(db, 'teams', teamId, 'members', uid);
      unsubMembers = safeOnSnapshot(myRef, async snap => {
        const memberDocs = snap.exists() ? [{
          id: uid,
          ...snap.data()
        }] : [];
        await hydrateProfiles(memberDocs, adminUids);
      }, err => {
        if (err?.code === 'permission-denied') return;
        if (isRecoverableFirestoreListenTargetError(err)) return;
        setError(err.message);
      });
    }
    return () => {
      cancelled = true;
      if (unsubMembers) unsubMembers();
    };
  }, [adminIdsKey, authLoading, ownPermissionKeysKey, teamId, teamReady, userId]);
  return {
    team,
    setTeam,
    memberProfiles,
    adminProfiles,
    loading,
    error
  };
}
